import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { classes } from "portal/theme/theme";
import { ImageQualityExamples, TransBold } from "./ImageQualityExamples";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InstructionsIcon from "@mui/icons-material/TipsAndUpdates";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import React, { FC, PropsWithChildren, useState } from "react";
import WarningIcon from "@mui/icons-material/WarningAmber";
import XIcon from "@mui/icons-material/Close";

export const PlantProfileInstructionsDialog: FC = () => {
  const { t } = useTranslation();
  const muiTheme = useTheme();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        color="inherit"
        startIcon={<InstructionsIcon />}
        onClick={() => setOpen(true)}
      >
        <span className="hidden md:block">
          {t("components.categoryCollectionProfile.instructions.trigger")}
        </span>
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg">
        <DialogTitle className="flex justify-between items-baseline">
          <InstructionDialogTitle>
            {t(
              "components.categoryCollectionProfile.instructions.titles.dialogTitle"
            )}
          </InstructionDialogTitle>
          <IconButton aria-label="close" onClick={() => setOpen(false)}>
            <XIcon fontSize="large" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* Picking Tips section */}
          <InstructionSection className="mt-2 mb-4">
            <InstructionSectionTitle>
              {t(
                "components.categoryCollectionProfile.instructions.titles.quality"
              )}
            </InstructionSectionTitle>

            <Alert
              severity="info"
              className="italic rounded-md"
              style={{
                backgroundColor: muiTheme.palette.info.main,
                color: muiTheme.palette.info.contrastText,
              }}
              icon={
                <LightbulbIcon
                  fontSize="small"
                  style={{
                    color: muiTheme.palette.info.contrastText,
                  }}
                />
              }
            >
              {t("components.categoryCollectionProfile.instructions.callout")}
            </Alert>

            <ol className="list-decimal ml-2 space-y-2">
              <li>
                <TransBold i18nKey="components.categoryCollectionProfile.instructions.classificationNumber" />
              </li>
              <li>
                <TransBold i18nKey="components.categoryCollectionProfile.instructions.confidence" />
              </li>
              <li>
                {t(
                  "components.categoryCollectionProfile.instructions.usage.save"
                )}
              </li>
            </ol>

            <Typography
              component="p"
              className="mt-2 border-solid border-carbon-orange p-1"
            >
              <TransBold i18nKey="components.categoryCollectionProfile.instructions.implementation" />
            </Typography>
          </InstructionSection>

          <ImageQualityExamples />
          {/* TroubleShooting Section */}
          <Accordion className="mt-4" defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                variant="h2"
                className={classes(
                  instructionHeaderClassNames,
                  "text-3xl flex items-center gap-2 pb-0"
                )}
              >
                <WarningIcon color="warning" fontSize="inherit" />
                {t(
                  "components.categoryCollectionProfile.instructions.troubleshooting.accordionTitle"
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className="flex flex-col gap-6">
              {/* Shooting Crop */}
              <div>
                <Typography
                  variant="h3"
                  className={classes(instructionHeaderClassNames, "text-xl")}
                >
                  {t(
                    "components.categoryCollectionProfile.instructions.troubleshooting.shootingCrop.title"
                  )}
                </Typography>
                <ul className="space-y-2">
                  <li>
                    {t(
                      "components.categoryCollectionProfile.instructions.troubleshooting.shootingCrop.step1"
                    )}
                  </li>
                  <li>
                    {t(
                      "components.categoryCollectionProfile.instructions.troubleshooting.shootingCrop.step2"
                    )}
                  </li>
                  <li>
                    {t(
                      "components.categoryCollectionProfile.instructions.troubleshooting.shootingCrop.step3"
                    )}
                  </li>
                  <li>
                    {t(
                      "components.categoryCollectionProfile.instructions.troubleshooting.shootingCrop.step4"
                    )}
                  </li>
                  <li>
                    {t(
                      "components.categoryCollectionProfile.instructions.troubleshooting.shootingCrop.step5"
                    )}
                  </li>
                </ul>
                <TroubleshootingNewProfile />
              </div>

              {/* Missing Weeds */}
              <div>
                <Typography
                  variant="h3"
                  className={classes(instructionHeaderClassNames, "text-xl")}
                >
                  {t(
                    "components.categoryCollectionProfile.instructions.troubleshooting.missingWeeds.title"
                  )}
                </Typography>
                <ul className="space-y-2">
                  <li>
                    {t(
                      "components.categoryCollectionProfile.instructions.troubleshooting.missingWeeds.step1"
                    )}
                  </li>
                  <li>
                    {t(
                      "components.categoryCollectionProfile.instructions.troubleshooting.missingWeeds.step2"
                    )}
                  </li>
                  <li>
                    {t(
                      "components.categoryCollectionProfile.instructions.troubleshooting.missingWeeds.step3"
                    )}
                  </li>
                  <li>
                    {t(
                      "components.categoryCollectionProfile.instructions.troubleshooting.missingWeeds.step4"
                    )}
                  </li>
                  <li>
                    {t(
                      "components.categoryCollectionProfile.instructions.troubleshooting.missingWeeds.step5"
                    )}
                  </li>
                  <li>
                    {t(
                      "components.categoryCollectionProfile.instructions.troubleshooting.missingWeeds.step6"
                    )}
                  </li>
                  <li>
                    {t(
                      "components.categoryCollectionProfile.instructions.troubleshooting.missingWeeds.step7"
                    )}
                  </li>
                </ul>
                <TroubleshootingNewProfile />
              </div>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
      </Dialog>
    </>
  );
};

const TroubleshootingNewProfile: FC = () => {
  const muiTheme = useTheme();
  return (
    <Alert
      severity="info"
      className="italic rounded-md"
      style={{
        backgroundColor: muiTheme.palette.info.main,
        color: muiTheme.palette.info.contrastText,
      }}
      icon={
        <LightbulbIcon
          fontSize="small"
          style={{
            color: muiTheme.palette.info.contrastText,
          }}
        />
      }
    >
      {t(
        "components.categoryCollectionProfile.instructions.troubleshooting.newProfile"
      )}
    </Alert>
  );
};

export const InstructionSection: FC<
  PropsWithChildren & { className?: string }
> = ({ children, className }) => (
  <Paper variant="section" className={classes("p-2 rounded-md", className)}>
    {children}
  </Paper>
);

export const instructionHeaderClassNames =
  "font-poppins normal-case font-bold pb-2";

export const InstructionDialogTitle: FC<PropsWithChildren> = ({ children }) => (
  <Typography
    variant="h1"
    className={classes(instructionHeaderClassNames, "text-[32px]")}
  >
    {children}
  </Typography>
);

export const InstructionSectionTitle: FC<PropsWithChildren> = ({
  children,
}) => (
  <Typography
    variant="h2"
    className={classes(instructionHeaderClassNames, "text-3xl")}
  >
    {children}
  </Typography>
);
